// Slide 3 of a product card's swipe track ships src-less (data-src/-srcset/-sizes)
// because loading="lazy" does not defer horizontally-offscreen slides: WebKit fetches
// them all up front, which jetsams iOS Safari on a full collection page. Promote a
// whole track the first time the shopper touches or scrolls it: the shopper must swipe
// past slide 2 before reaching slide 3, so the fetch has time to land.
function promoteSwipeSlides(track) {
  track.querySelectorAll('img[data-src]').forEach((image) => {
    if (image.dataset.srcset) image.srcset = image.dataset.srcset;
    if (image.dataset.sizes) image.sizes = image.dataset.sizes;
    image.src = image.dataset.src;
    delete image.dataset.srcset;
    delete image.dataset.sizes;
    delete image.dataset.src;
  });
}

// Once per session the first swipe card meaningfully on screen "peeks": the track
// eases left to reveal the edge of photo 2, then settles back, hinting that it swipes.
// A shopper who has already touched or scrolled any track never sees it.
const NUDGE_KEY = 'naomi:card-swipe-nudged';
let swipeDiscovered = false;

function markSwipeDiscovered() {
  if (swipeDiscovered) return;
  swipeDiscovered = true;
  try {
    sessionStorage.setItem(NUDGE_KEY, '1');
  } catch (error) {
    // Storage blocked: the in-memory flag still stops a repeat on this page.
  }
}

function swipeAlreadyDiscovered() {
  if (swipeDiscovered) return true;
  try {
    return sessionStorage.getItem(NUDGE_KEY) !== null;
  } catch (error) {
    // Cannot remember across pages, so skip rather than nudge on every page view.
    return true;
  }
}

function peekSwipeTrack(track) {
  const distance = track.clientWidth * 0.18;
  const outMs = 380;
  const holdMs = 240;
  const backMs = 380;
  const totalMs = outMs + holdMs + backMs;
  // Sine ease-in-out: soft start and soft landing on both legs.
  const ease = (t) => 0.5 - Math.cos(Math.PI * t) / 2;
  let start = null;
  let frame = 0;

  // Inline override: mandatory snap would yank programmatic scrollLeft back to 0.
  track.style.scrollSnapType = 'none';
  const finish = () => {
    cancelAnimationFrame(frame);
    track.removeEventListener('touchstart', abort);
    track.style.scrollSnapType = '';
  };
  // The shopper takes over mid-peek: stop where it is and hand the scroll back.
  function abort() {
    finish();
  }
  track.addEventListener('touchstart', abort, { passive: true, once: true });

  const step = (now) => {
    if (start === null) start = now;
    const elapsed = now - start;
    let offset;
    if (elapsed < outMs) offset = ease(elapsed / outMs);
    else if (elapsed < outMs + holdMs) offset = 1;
    else offset = 1 - ease(Math.min((elapsed - outMs - holdMs) / backMs, 1));
    track.scrollLeft = distance * offset;
    if (elapsed < totalMs) frame = requestAnimationFrame(step);
    else finish();
  };
  frame = requestAnimationFrame(step);
}

function initSwipeNudge() {
  const mobile = window.matchMedia('(max-width: 989px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (!('IntersectionObserver' in window) || !mobile.matches || reducedMotion.matches || swipeAlreadyDiscovered()) return;

  // Only the tracks present now: one nudge per session, so facets.js re-renders need
  // no re-observing.
  const tracks = document.querySelectorAll('.media--swipe');
  if (!tracks.length) return;

  const timers = new Map();
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach(({ target, isIntersecting, intersectionRatio }) => {
        clearTimeout(timers.get(target));
        timers.delete(target);
        if (!isIntersecting || intersectionRatio < 0.6) return;
        // 600ms of continuous visibility, so a card scrolled past mid-flick is skipped.
        timers.set(
          target,
          setTimeout(() => {
            timers.forEach(clearTimeout);
            timers.clear();
            observer.disconnect();
            if (!mobile.matches || reducedMotion.matches || swipeAlreadyDiscovered()) return;
            markSwipeDiscovered();
            peekSwipeTrack(target);
          }, 600)
        );
      });
    },
    { threshold: 0.6 }
  );
  tracks.forEach((track) => observer.observe(track));
}

// Deferred script: the document is parsed by the time this runs.
initSwipeNudge();

// Delegated so facets.js re-rendering the grid needs no re-wiring. `scroll` does not
// bubble, hence the capture phase.
document.addEventListener(
  'touchstart',
  (event) => {
    const track = event.target.closest?.('.media--swipe');
    if (!track) return;
    markSwipeDiscovered();
    promoteSwipeSlides(track);
  },
  { passive: true, capture: true }
);

document.addEventListener(
  'scroll',
  (event) => {
    const track = event.target;
    if (!track.matches?.('.media--swipe')) return;
    // The peek nudge's own scrollLeft writes land here too; harmless, since the key is
    // already set at nudge start and promoting just loads slide 3 a little early.
    markSwipeDiscovered();
    promoteSwipeSlides(track);
  },
  { passive: true, capture: true }
);
